import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import IndoorPositioningHistory from "../../../../api/IndoorPositioningHistory";
import { resolveFrame, getPrecedingFrames } from "../utils/resolveFrame";
import { buildCanvasPersonnel } from "../utils/buildCanvasPersonnel";
import { buildCanvasEquipment } from "../utils/buildCanvasEquipment";

const isAbort = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

// Fetches /history/frames for the current (range, intervalS) and exposes the
// raw frames via a ref (framesRef -- read on every playhead tick, never a
// dependency of a render) plus two pure lookups, `personnelAt(playheadMs)`
// and `equipmentAt(playheadMs)`, that wrap resolveFrame + the respective
// buildCanvas* helper for callers (the canvas host). `include=equipment` is
// requested unconditionally: the workspace always wants machines alongside
// personnel, and per the contract a caller that doesn't ask for it gets
// today's response back byte-for-byte, so there is no cost to always asking.
export const useReplayFrames = (
  machineID,
  range,
  intervalS,
  onIntervalClamped,
) => {
  const [roster, setRoster] = useState([]);
  const [equipmentRoster, setEquipmentRoster] = useState([]);
  const [intervalUsed, setIntervalUsed] = useState(intervalS);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const framesRef = useRef([]);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!machineID || !range) return undefined;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    IndoorPositioningHistory.GetFrames(
      machineID,
      range.startMs,
      range.endMs,
      intervalS,
      controller.signal,
      "equipment",
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        if (response.code === 200 && response.data) {
          framesRef.current = response.data.frames || [];
          setRoster(response.data.personnel || []);
          setEquipmentRoster(response.data.equipment || []);
          const used = response.data.intervalUsed;
          setIntervalUsed(used);
          if (used != null && used !== intervalS) {
            message.info(`已調整為 ${used} 秒（範圍過長）`);
            // Sync the 精細度 control to what was actually served. Done HERE,
            // in the response of the request that got clamped -- a
            // state-watching effect comparing current intervalS against the
            // last response's intervalUsed would clobber the user's NEXT
            // selection before its own fetch returns. The write-back
            // triggers one follow-up fetch at the served interval, which the
            // server answers identically (used === requested), so it
            // settles in one round.
            onIntervalClamped?.(used);
          }
        } else {
          framesRef.current = [];
          setRoster([]);
          setEquipmentRoster([]);
          // A toast alone is gone in 3s and leaves an unexplained empty
          // canvas; keep a persistent error the core renders inline.
          setError(response.message || "載入歷史軌跡失敗");
          message.error(response.message || "載入歷史軌跡失敗");
        }
      })
      .catch((err) => {
        if (isAbort(err)) return;
        framesRef.current = [];
        setRoster([]);
        setEquipmentRoster([]);
        setError("載入歷史軌跡失敗");
        message.error("載入歷史軌跡失敗");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [machineID, range?.startMs, range?.endMs, intervalS, onIntervalClamped]);

  const personnelAt = useCallback(
    (playheadMs) => {
      const frames = framesRef.current;
      const frame = resolveFrame(frames, playheadMs);
      const prevFrames = frame ? getPrecedingFrames(frames, frame, 5) : [];
      return buildCanvasPersonnel(
        frame,
        prevFrames,
        roster,
        playheadMs,
        intervalUsed * 1000,
      );
    },
    [roster, intervalUsed],
  );

  const equipmentAt = useCallback(
    (playheadMs) => {
      const frames = framesRef.current;
      const frame = resolveFrame(frames, playheadMs);
      const prevFrames = frame ? getPrecedingFrames(frames, frame, 5) : [];
      return buildCanvasEquipment(
        frame,
        prevFrames,
        equipmentRoster,
        playheadMs,
        intervalUsed * 1000,
      );
    },
    [equipmentRoster, intervalUsed],
  );

  return {
    framesRef,
    roster,
    equipmentRoster,
    intervalUsed,
    loading,
    error,
    personnelAt,
    equipmentAt,
  };
};
